import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';

import s from './AppSearchMobile.module.scss';
import { DebouncedInput } from '@/components/core/application-search/components/DebouncedInput';
import { FullSearchResults } from '@/components/core/application-search/components/FullSearchResults';
import { RecentSearch } from '@/components/core/application-search/components/RecentSearch';
import { AiSearchEntries } from '@/components/core/application-search/components/AiSearchEntries';
import { RecentAiChats } from '@/components/core/application-search/components/RecentAiChats';
import { openAiSearch } from '@/components/utils/openAiSearch';
import { askAboutItem } from '@/components/core/application-search/utils/askAboutItem';
import { REOPEN_HEADER_SEARCH_EVENT } from '@/components/constants/aiSearchHandoff';
import { useFullApplicationSearch } from '@/services/search/hooks/useFullApplicationSearch';
import { useRecordRecentSearch } from '@/services/search/hooks/useRecordRecentSearch';
import { useRouter, useSearchParams } from 'next/navigation';

interface Props {
  isLoggedIn: boolean;
}

export const AppSearchMobile = ({ isLoggedIn }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const open = searchParams.get('searchState') === 'open';

  const [searchTerm, setSearchTerm] = useState('');
  const { data } = useFullApplicationSearch(searchTerm);

  /* Recent used to be written by that hook's fetcher; it is the caller's job
     now. See useRecordRecentSearch. */
  useRecordRecentSearch(searchTerm);
  const [activeCategory, setActiveCategory] = React.useState<
    'top' | 'members' | 'teams' | 'projects' | 'forumThreads' | 'events' | null
  >('top');

  const handleClose = useCallback(() => {
    // Preserve existing filter parameters when removing searchState
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.delete('searchState');
    const newUrl = currentParams.toString()
      ? `${window.location.pathname}?${currentParams.toString()}`
      : window.location.pathname;
    router.replace(newUrl, { scroll: false });

    setSearchTerm('');
  }, [router, searchParams]);

  const handleChange = useCallback((val: string) => {
    setSearchTerm(val);
  }, []);

  const openAi = (options: { question?: string; threadId?: string; showHistory?: boolean }) => {
    openAiSearch({ router, term: searchTerm, ...options });
    setSearchTerm('');
  };

  // "Back to search" on the AI Search page reopens this sheet (through ?searchState=open) with the term the member left with.
  useEffect(() => {
    const reopen = (e: Event) => setSearchTerm((e as CustomEvent<{ term: string }>).detail.term);
    document.addEventListener(REOPEN_HEADER_SEARCH_EVENT, reopen);
    return () => document.removeEventListener(REOPEN_HEADER_SEARCH_EVENT, reopen);
  }, []);

  function renderContent() {
    if (!searchTerm) {
      return (
        <div className={s.fullBleed}>
          <AiSearchEntries term="" onAsk={(question) => openAi({ question })} />
          <div className={s.idle}>
            {isLoggedIn && <RecentSearch onSelect={handleChange} />}
            {isLoggedIn && (
              <RecentAiChats
                onOpenChat={(threadId) => openAi({ threadId })}
                onShowAll={() => openAi({ showHistory: true })}
              />
            )}
            <p className={s.hint}>Search members, teams, projects, events and forum posts.</p>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className={s.fullBleed}>
          <AiSearchEntries term={searchTerm} results={data} onAsk={(question) => openAi({ question })} />
        </div>
        <FullSearchResults
          searchTerm={searchTerm}
          onTryAiSearch={() => openAi({ question: searchTerm })}
          onAskAi={(item) => openAi({ question: askAboutItem(item) })}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
        />
      </>
    );
  }

  return (
    <div className={s.root}>
      <button
        className={s.toggleButton}
        onClick={() => {
          // Preserve existing filter parameters when adding searchState
          const currentParams = new URLSearchParams(searchParams.toString());
          currentParams.set('searchState', 'open');
          router.replace(`${window.location.pathname}?${currentParams.toString()}`, { scroll: false });
        }}
      >
        <SearchIcon />
      </button>
      {open && (
        <div className={s.wrapper}>
          <div className={s.top}>
            <div className={s.header}>
              <span className={s.title}>Search</span>
              <button className={s.closeButton} onClick={handleClose}>
                <Image
                  src="/icons/close-gray.svg"
                  alt="Close"
                  width={20}
                  height={20}
                  style={{ pointerEvents: 'none' }}
                />
              </button>
            </div>
            <div className={s.divider} />
            <div className={s.inputSection}>
              <DebouncedInput
                onChange={handleChange}
                placeholder="Search or ask AI Search a question"
                value={searchTerm}
              />
            </div>
            <div className={s.divider} />
          </div>
          <div className={s.content}>{renderContent()}</div>
        </div>
      )}
    </div>
  );
};

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.9421 17.0577L14.0304 13.1468C15.1642 11.7856 15.7295 10.0398 15.6089 8.27238C15.4882 6.505 14.6908 4.85217 13.3825 3.65772C12.0743 2.46328 10.3559 1.8192 8.58486 1.85944C6.81382 1.89969 5.12647 2.62118 3.87383 3.87383C2.62118 5.12647 1.89969 6.81382 1.85944 8.58486C1.8192 10.3559 2.46328 12.0743 3.65772 13.3825C4.85217 14.6908 6.505 15.4882 8.27238 15.6089C10.0398 15.7295 11.7856 15.1642 13.1468 14.0304L17.0577 17.9421C17.1158 18.0002 17.1848 18.0463 17.2606 18.0777C17.3365 18.1091 17.4178 18.1253 17.4999 18.1253C17.5821 18.1253 17.6634 18.1091 17.7392 18.0777C17.8151 18.0463 17.8841 18.0002 17.9421 17.9421C18.0002 17.8841 18.0463 17.8151 18.0777 17.7392C18.1091 17.6634 18.1253 17.5821 18.1253 17.4999C18.1253 17.4178 18.1091 17.3365 18.0777 17.2606C18.0463 17.1848 18.0002 17.1158 17.9421 17.0577ZM3.12493 8.74993C3.12493 7.63741 3.45483 6.54988 4.07292 5.62485C4.691 4.69982 5.56951 3.97885 6.59734 3.55311C7.62517 3.12737 8.75617 3.01598 9.84732 3.23302C10.9385 3.45006 11.9407 3.98579 12.7274 4.77246C13.5141 5.55913 14.0498 6.56141 14.2669 7.65255C14.4839 8.74369 14.3725 9.87469 13.9468 10.9025C13.521 11.9304 12.8 12.8089 11.875 13.427C10.95 14.045 9.86245 14.3749 8.74993 14.3749C7.2586 14.3733 5.82882 13.7801 4.77429 12.7256C3.71975 11.671 3.12659 10.2413 3.12493 8.74993Z"
      fill="#455468"
    />
  </svg>
);
